import { z } from "zod";
import { AgencyIdSchema, ProjectIdSchema } from "../primitives/ids.js";
import { StudioSimpleProjectPhaseIdSchema } from "./studio-project-lifecycle.js";
/** Exact Today section order (Canonical UX §5.4 / Step 5 Plan). */
export const TODAY_SECTION_ORDER = [
    "start-here",
    "needs-attention",
    "waiting",
    "builds-approvals-or-failures",
    "other-projects",
    "quick-note",
];
export const TodaySectionIdSchema = z.enum(TODAY_SECTION_ORDER);
export const StudioTodaySectionSkeletonSchema = z
    .object({
    sectionId: TodaySectionIdSchema,
    items: z.array(z.unknown()).default([]),
})
    .strict();
/**
 * Studio Today response skeleton — sections always appear in TODAY_SECTION_ORDER.
 * Compositors fill items; contracts lock section identity and order.
 */
export const StudioTodayResponseSchema = z
    .object({
    schemaId: z.literal("studio.today.v1"),
    agencyId: AgencyIdSchema,
    asOf: z.string().datetime(),
    sections: z.array(StudioTodaySectionSkeletonSchema).length(TODAY_SECTION_ORDER.length),
})
    .strict()
    .superRefine((value, ctx) => {
    for (let i = 0; i < TODAY_SECTION_ORDER.length; i += 1) {
        if (value.sections[i]?.sectionId !== TODAY_SECTION_ORDER[i]) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "today_section_order_mismatch",
                path: ["sections", i, "sectionId"],
            });
        }
    }
});
export const DueBucketSchema = z.enum(["NOT_SET", "ON_TRACK", "DUE_SOON", "OVERDUE"]);
export const RiskLevelSchema = z.enum(["LOW", "MEDIUM", "HIGH"]);
export const StudioProjectPipelineStageFilterSchema = z.enum([
    "all",
    "waiting_for_client",
    "review_and_build",
    "generating",
    "preview_live",
    "client_approved",
    "packaging",
    "handoff_ready",
]);
export const StudioProjectPipelineDueFilterSchema = z.enum([
    "all",
    "NOT_SET",
    "ON_TRACK",
    "DUE_SOON",
    "OVERDUE",
]);
export const StudioProjectPipelineRiskFilterSchema = z.enum(["all", "LOW", "MEDIUM", "HIGH"]);
export const StudioProjectPipelineClientActionFilterSchema = z.enum([
    "all",
    "outstanding",
    "none",
]);
export const StudioProjectPipelineFilterSchema = z
    .object({
    stage: StudioProjectPipelineStageFilterSchema.default("all"),
    due: StudioProjectPipelineDueFilterSchema.default("all"),
    risk: StudioProjectPipelineRiskFilterSchema.default("all"),
    clientAction: StudioProjectPipelineClientActionFilterSchema.default("all"),
})
    .strict();
/** Phase ids remain aligned with StudioSimpleProjectPhaseId for stage filter (excluding `all`). */
export const StudioProjectPipelineStageSchema = StudioSimpleProjectPhaseIdSchema;
const DUE_SOON_MS = 72 * 60 * 60 * 1000;
/**
 * Server-side due bucket. Frontend must not calculate due.
 * - NOT_SET: no persisted due date
 * - OVERDUE: asOf is after due date
 * - DUE_SOON: due within 72 hours (inclusive of exact due instant)
 * - ON_TRACK: due after 72 hours
 */
export function computeDueBucket(deliveryDueAt, asOf) {
    if (deliveryDueAt === null) {
        return "NOT_SET";
    }
    const dueMs = deliveryDueAt.getTime();
    const asOfMs = asOf.getTime();
    if (asOfMs > dueMs) {
        return "OVERDUE";
    }
    const msUntilDue = dueMs - asOfMs;
    if (msUntilDue <= DUE_SOON_MS) {
        return "DUE_SOON";
    }
    return "ON_TRACK";
}
export const ACTIVITY_LIST_DEFAULT_LIMIT = 50;
export const ACTIVITY_LIST_MAX_LIMIT = 100;
export const ActivityListQuerySchema = z
    .object({
    cursor: z.string().min(1).optional(),
    limit: z.number().int().min(1).max(ACTIVITY_LIST_MAX_LIMIT).default(ACTIVITY_LIST_DEFAULT_LIMIT),
})
    .strict();
export const PORTFOLIO_LIST_DEFAULT_LIMIT = 25;
export const PORTFOLIO_LIST_MAX_LIMIT = 100;
export const PortfolioListQuerySchema = z
    .object({
    cursor: z.string().min(1).optional(),
    limit: z.number().int().min(1).max(PORTFOLIO_LIST_MAX_LIMIT).default(PORTFOLIO_LIST_DEFAULT_LIMIT),
})
    .strict();
/** Canonical Project Room tabs (Step 5). */
export const CANONICAL_PROJECT_ROOM_TABS = [
    "overview",
    "business",
    "website",
    "changes",
    "approval-delivery",
    "notes",
    "activity",
    "advanced",
];
export const CanonicalProjectRoomTabSchema = z.enum(CANONICAL_PROJECT_ROOM_TABS);
/**
 * Legacy V10 section aliases → canonical tab. Aliases do not create a second truth source.
 */
export const PROJECT_ROOM_TAB_ALIASES = {
    setup: "overview",
    "truth-proof": "business",
    review: "website",
    "l2-edits": "changes",
    approval: "approval-delivery",
    "handoff-deploy": "approval-delivery",
    "live-care": "overview",
};
export function resolveProjectRoomTab(section) {
    const alias = PROJECT_ROOM_TAB_ALIASES[section];
    if (alias) {
        return alias;
    }
    return section;
}
export const NoteTypeSchema = z.enum(["INTERNAL", "TASK"]);
/** Quick Note always creates INTERNAL (not TASK / CLIENT_SHARED). */
export const QuickNoteCreateRequestSchema = z
    .object({
    schemaId: z.literal("step5.quickNoteCreateRequest.v1").optional(),
    projectId: ProjectIdSchema,
    body: z.string().min(1).max(4000),
})
    .strict();
/** Exact success wording after Note + audit commit. */
export function QUICK_NOTE_SUCCESS_COPY(projectLabel) {
    return `Internal note saved in ${projectLabel} Notes.`;
}
export const StudioBusinessStatusSchema = z.enum(["ACTIVE", "ARCHIVED"]);
export const StudioBusinessRecordSchema = z
    .object({
    schemaId: z.literal("studio.business.v1"),
    agencyId: AgencyIdSchema,
    businessId: z.string().min(1).max(80),
    displayName: z.string().min(1).max(160),
    clientEntityName: z.string().min(1).max(160).nullable(),
    status: StudioBusinessStatusSchema,
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    projectIds: z.array(ProjectIdSchema).default([]),
})
    .strict();
export const StudioBusinessListResponseSchema = z
    .object({
    schemaId: z.literal("studio.businessList.v1"),
    agencyId: AgencyIdSchema,
    items: z.array(StudioBusinessRecordSchema),
})
    .strict();
export const StudioBusinessCreateRequestSchema = z
    .object({
    schemaId: z.literal("studio.businessCreateRequest.v1"),
    displayName: z.string().min(1).max(160),
    clientEntityName: z.string().min(1).max(160).optional(),
    businessId: z.string().min(1).max(80).optional(),
    evidence: z
        .object({
        domain: z.string().min(1).max(240).optional(),
        contact: z.string().min(1).max(240).optional(),
        location: z.string().min(1).max(240).optional(),
    })
        .strict()
        .optional(),
})
    .strict();
//# sourceMappingURL=step5-studio-surfaces.js.map